import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import IconButton from "../../../DesignSystem/Buttons/IconButton";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import Banner from "../../../DesignSystem/Banner/Banner";
import AddCharge from "../../../Accounts/MonthlyPayments/pages/AddCharge";
import AddPayment from "../../../Accounts/MonthlyPayments/pages/AddPayment";
import { subscribeMonthlyManualCharges } from "../../../Accounts/MonthlyPayments/services/discountChargeService";
import { subscribeMonthlyManualPayments } from "../../../Accounts/MonthlyPayments/services/manualPaymentService";
import { chargesForMonth, lastWorkingDayOfMonth } from "../../../Accounts/MonthlyPayments/utils/chargeDates";
import { paymentsForMonth } from "../../../Accounts/MonthlyPayments/utils/paymentDates";
import { getAdviserChargeMonthOptions } from "../../../Accounts/MonthlyPayments/utils/adviserChargeMonths";
import { getIncomePeriods } from "../../../Accounts/utils/incomePeriods";
import {
  canOverrideClosedChargeMonths,
  isChargeMonthClosed,
} from "../../../Accounts/MonthlyPayments/utils/closedChargeMonths";
import {
  canAccessManualCharge,
  getAdviserChargeAccess,
} from "../../../Accounts/MonthlyPayments/utils/adviserChargeAccess";
import { useUserEmail } from "../../../SignIn/UserEmailContext";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import "../../../../css/Finance/MonthlyPayments.css";

const keyOf = (value) => String(value || "").trim().toLowerCase();
const dateText = (value) => {
  const date = value?.toDate?.() || new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const money = (value) => Number(value || 0).toLocaleString("en-GB", {
  style: "currency",
  currency: "GBP",
});
const isZeroOpeningBalance = (entry) => {
  const isOpeningBalance =
    entry.paymentType === "opening_balance" ||
    entry.recordType === "opening_balance" ||
    entry.source === "opening_balance_bulk_upload" ||
    String(entry.description || "").trim().toLowerCase() ===
      "opening balance brought forward";
  const amount = entry.openingBalanceAmount ?? entry.amountPaid;
  return isOpeningBalance && Number(amount || 0) === 0;
};
const AdviserChargesTab = ({ adviserKey, details, header }) => {
  const [isAddChargeOpen, setIsAddChargeOpen] = useState(false);
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const options = getAdviserChargeMonthOptions();
    return options[0]?.id || "";
  });
  const [charges, setCharges] = useState([]);
  const [payments, setPayments] = useState([]);
  const [incomePeriods, setIncomePeriods] = useState([]);
  const { userEmail } = useUserEmail();
  const { userPermissions = [] } = useUserPermissions();
  const adviser = useMemo(() => ({
    id: details?.adviserId || adviserKey,
    email: header?.email || details?.email || adviserKey,
    name: header?.name || details?.name || details?.adviser || adviserKey,
    brand:
      header?.brandName ||
      details?.brandName ||
      details?.brand ||
      details?.practice ||
      "",
  }), [adviserKey, details, header]);
  const chargeAccess = useMemo(
    () => getAdviserChargeAccess(userPermissions, userEmail),
    [userEmail, userPermissions],
  );
  useEffect(
    () =>
      subscribeMonthlyManualCharges(setCharges, () => setCharges([]), {
        permissions: userPermissions,
        userEmail,
        adviserEmail: adviser.email,
      }),
    [adviser.email, userEmail, userPermissions],
  );
  useEffect(
    () => subscribeMonthlyManualPayments(setPayments, () => setPayments([])),
    [],
  );
  useEffect(() => {
    let active = true;
    getIncomePeriods().then(
      (periods) => {
        if (active) setIncomePeriods(periods);
      },
      () => {
        if (active) setIncomePeriods([]);
      },
    );
    return () => { active = false; };
  }, []);
  const canSeeEntry = (entry) =>
    canAccessManualCharge(entry, {
      permissions: userPermissions,
      userEmail,
      adviserEmail: adviser.email,
    });
  const allAdviserCharges = charges
    .filter(canSeeEntry)
    .sort((left, right) => String(right.date || "").localeCompare(String(left.date || "")));
  const allAdviserPayments = payments
    .filter((entry) => keyOf(entry.adviserEmail) === keyOf(adviser.email))
    .filter(canSeeEntry)
    .filter((entry) => !isZeroOpeningBalance(entry))
    .sort((left, right) => String(right.date || "").localeCompare(String(left.date || "")));
  const monthOptions = getAdviserChargeMonthOptions(new Date(), [
    ...allAdviserCharges,
    ...allAdviserPayments,
  ]);
  const adviserCharges = chargesForMonth(
    allAdviserCharges.map((entry) => {
      const isRecurring = entry.billingType === "recurring";
      const isLoanRepayment = entry.chargeTypeId === "loan-repayment";
      return {
        ...entry,
        billingStartDate: entry.chargeFrom || entry.date,
        fromDate: entry.chargeFrom || entry.date,
        recurrence: isRecurring
          ? isLoanRepayment
            ? "installment"
            : "monthly"
          : "one_time",
        installments: Number(entry.repaymentInstalments || 1),
        amountCharged: isRecurring
          ? Number(
              entry.currentMonthlyRepaymentAmount ||
                entry.monthlyRepaymentAmount ||
                entry.amountCharged ||
                0,
            )
          : Number(entry.amountCharged || 0),
      };
    }),
    selectedMonth,
  );
  const adviserPayments = paymentsForMonth(allAdviserPayments, selectedMonth);
  const totalCharges = adviserCharges.reduce(
    (total, entry) => total + Number(entry.amountCharged || 0),
    0,
  );
  const totalPayments = adviserPayments.reduce(
    (total, entry) =>
      total +
      (entry.paymentType === "opening_balance"
        ? 0
        : Number(entry.amountPaid || 0)),
    0,
  );
  const defaultRecurringDate = lastWorkingDayOfMonth(selectedMonth);
  const selectedMonthClosed = isChargeMonthClosed(incomePeriods, selectedMonth);
  const canOverrideClosedCharges = canOverrideClosedChargeMonths(userPermissions);
  const chargesLocked = selectedMonthClosed && !canOverrideClosedCharges;

  return (
    <section className="monthly-payments-page adviser-charges-tab fit-column gap-l width-100" aria-label="Adviser charges">
      <div className="space-between-row align-end gap-m width-100">
        <div className="adviser-charges-month-filter">
          <CustomSelect
            label="Month"
            options={monthOptions}
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            clearable={false}
            searchable={false}
          />
        </div>
        {chargeAccess.canManageCharges ? (
          <div className="row justify-flex-end align-center gap-s">
            <SecondaryButton
              type="button"
              label="ADD PAYMENT"
              disabled={selectedMonthClosed}
              onClick={() => {
                setSelectedPayment(null);
                setIsAddPaymentOpen(true);
              }}
            />
            <PrimaryButton
              type="button"
              label="ADD CHARGE"
              disabled={chargesLocked}
              onClick={() => {
                setSelectedCharge(null);
                setIsAddChargeOpen(true);
              }}
            />
          </div>
        ) : null}
      </div>
      {selectedMonthClosed ? (
        <Banner
          type="info"
          message={
            canOverrideClosedCharges
              ? "This month is closed. Finance override is enabled for charges; payments remain read-only."
              : "This month is closed. Charges and payments are read-only."
          }
        />
      ) : null}

      <section className="fit-column gap-s width-100">
        <h4 className="FP_Subtitle_Semibold text-black text-align-left width-100">Charges</h4>
        <div className="website-dashboard-card opening-balances-card">
          <div className="website-table-wrap table-scroll-container">
            <table className="website-table" aria-label="Manual charges">
            <thead>
              <tr>
                <th className="FP_Help_Text_Semibold uppercase">Date</th>
                <th className="FP_Help_Text_Semibold uppercase">Charge Type</th>
                <th className="FP_Help_Text_Semibold uppercase">Description</th>
                <th className="FP_Help_Text_Semibold uppercase">Client Name</th>
                <th className="FP_Help_Text_Semibold uppercase website-charge-group-divider text-align-right">Amount</th>
                <th className="FP_Help_Text_Semibold uppercase">Added By</th>
                {chargeAccess.canManageCharges ? (
                  <th className="FP_Help_Text_Semibold uppercase table-action-column">Action</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {adviserCharges.length ? adviserCharges.map((entry) => (
                <tr key={entry.id}>
                  <td className="FP_Help_Text black">{dateText(entry.date)}</td>
                  <td className="FP_Help_Text black">{entry.chargeType || "-"}</td>
                  <td className="FP_Help_Text black">{entry.description || "-"}</td>
                  <td className="FP_Help_Text black">{entry.client || entry.clientName || "-"}</td>
                  <td className="FP_Help_Text black website-charge-group-divider text-align-right">{money(entry.amountCharged)}</td>
                  <td className="FP_Help_Text black">{entry.createdBy?.name || entry.createdBy?.email || "-"}</td>
                  {chargeAccess.canManageCharges ? (
                  <td className="table-action-column">
                    {!chargesLocked ? (
                      <IconButton
                        icon={<EditIcon aria-hidden="true" />}
                        label={`Edit ${entry.chargeType || "charge"}`}
                        onClick={() => {
                          setSelectedCharge(entry);
                          setIsAddChargeOpen(true);
                        }}
                      />
                    ) : null}
                  </td>
                  ) : null}
                </tr>
              )) : (
                <tr><td className="FP_Content" colSpan={chargeAccess.canManageCharges ? 7 : 6}>No charge entries available.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="website-charges-total-row">
                <td className="FP_Content_Semibold" colSpan="4">Total Charges</td>
                <td className="FP_Content_Semibold website-charge-group-divider text-align-right">{money(totalCharges)}</td>
                <td colSpan={chargeAccess.canManageCharges ? 2 : 1} />
              </tr>
            </tfoot>
            </table>
          </div>
        </div>
      </section>

      <section className="fit-column gap-s width-100">
        <h4 className="FP_Subtitle_Semibold text-black text-align-left width-100">Payments</h4>
        <div className="website-dashboard-card opening-balances-card">
          <div className="website-table-wrap table-scroll-container">
            <table className="website-table" aria-label="Manual payments">
            <thead>
              <tr>
                <th className="FP_Help_Text_Semibold uppercase">Date</th>
                <th className="FP_Help_Text_Semibold uppercase">Payment Type</th>
                <th className="FP_Help_Text_Semibold uppercase">Description</th>
                <th className="FP_Help_Text_Semibold uppercase website-charge-group-divider text-align-right">Amount</th>
                <th className="FP_Help_Text_Semibold uppercase">Added By</th>
                {chargeAccess.canManageCharges ? (
                  <th className="FP_Help_Text_Semibold uppercase table-action-column">Action</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {adviserPayments.length ? adviserPayments.map((entry) => (
                <tr key={entry.id}>
                  <td className="FP_Help_Text black">{dateText(entry.date)}</td>
                  <td className="FP_Help_Text black">
                    <div className="fit-column">
                      <span>
                        {entry.paymentType === "advance"
                          ? "Advance"
                          : entry.paymentType === "opening_balance"
                            ? "Opening Balance"
                            : "Additional Payment"}
                      </span>
                      {entry.paymentType !== "opening_balance" ? (
                        <span className="FP_Help_Text monthly-charge-paraplanner">
                          {entry.billingType === "recurring"
                            ? "Recurring"
                            : "One-Time"}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="FP_Help_Text black">{entry.description || "-"}</td>
                  <td className="FP_Help_Text black website-charge-group-divider text-align-right">
                    {money(
                      entry.paymentType === "opening_balance"
                        ? entry.openingBalanceAmount
                        : entry.amountPaid,
                    )}
                  </td>
                  <td className="FP_Help_Text black">{entry.createdBy?.name || entry.createdBy?.email || "-"}</td>
                  {chargeAccess.canManageCharges ? (
                  <td className="table-action-column">
                    {entry.paymentType === "opening_balance" || selectedMonthClosed ? null : (
                      <IconButton
                        icon={<EditIcon aria-hidden="true" />}
                        label={`Edit ${entry.paymentType === "advance" ? "advance" : "additional payment"}`}
                        onClick={() => {
                          setSelectedPayment(entry);
                          setIsAddPaymentOpen(true);
                        }}
                      />
                    )}
                  </td>
                  ) : null}
                </tr>
              )) : (
                <tr><td className="FP_Content" colSpan={chargeAccess.canManageCharges ? 6 : 5}>No payment entries available.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="website-charges-total-row">
                <td className="FP_Content_Semibold" colSpan="3">Total Payments</td>
                <td className="FP_Content_Semibold website-charge-group-divider text-align-right">{money(totalPayments)}</td>
                <td colSpan={chargeAccess.canManageCharges ? 2 : 1} />
              </tr>
            </tfoot>
            </table>
          </div>
        </div>
      </section>

      {isAddChargeOpen ? (
        createPortal(
          <div
            className="ParaplanningModalOverlay adviser-charge-modal-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsAddChargeOpen(false);
            }}
          >
            <AddCharge
              adviser={adviser}
              charge={selectedCharge}
              defaultDate={defaultRecurringDate}
              defaultRecurringDate={defaultRecurringDate}
              allowLoanRepayment={chargeAccess.canViewLoanRepayment}
              userPermissions={userPermissions}
              onClose={() => {
                setIsAddChargeOpen(false);
                setSelectedCharge(null);
              }}
            />
          </div>,
          document.body,
        )
      ) : null}

      {isAddPaymentOpen ? (
        <div
          className="ParaplanningModalOverlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsAddPaymentOpen(false);
          }}
        >
          <AddPayment
            adviser={adviser}
            payment={selectedPayment}
            initialType={selectedPayment?.paymentType || "additional_payment"}
            defaultDate={defaultRecurringDate}
            onClose={() => {
              setIsAddPaymentOpen(false);
              setSelectedPayment(null);
            }}
          />
        </div>
      ) : null}
    </section>
  );
};

export default AdviserChargesTab;
